/**
 * Mini-map for the THEATER Driver tab media slot (mockup .drv-map):
 * while the big video owns the stage, the media slot shows where the
 * followed car is. Lightweight SVG projection (equirectangular over the
 * data's own bounding box) instead of a second Leaflet instance — no
 * tiles, no remount cost when flipping theater tabs.
 *
 * Geometry source preference: raceline → device trail → all device
 * positions. Falls back to a grid placeholder when nothing has GPS yet.
 */

import React, { useMemo } from 'react';

import { EventLiveDevice } from '../../hooks/useEventLiveDevices';
import { GpsSummary } from '../../types';
import { displayCarNumber } from './carNumber';

const W = 300;
const H = 200;
const PAD = 18;

interface Projector {
  x: (lon: number) => number;
  y: (lat: number) => number;
}

function buildProjector(coords: Array<[number, number]>): Projector | null {
  if (coords.length === 0) return null;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLon = Infinity;
  let maxLon = -Infinity;
  for (const [lat, lon] of coords) {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
  }
  // Aspect-correct: compress longitude by cos(midLat) so the trace
  // isn't stretched at high latitudes, then letterbox into the box.
  const midLat = (minLat + maxLat) / 2;
  const lonScale = Math.cos((midLat * Math.PI) / 180);
  const spanLat = Math.max(maxLat - minLat, 1e-6);
  const spanLon = Math.max((maxLon - minLon) * lonScale, 1e-6);
  const scale = Math.min((W - 2 * PAD) / spanLon, (H - 2 * PAD) / spanLat);
  const offX = (W - spanLon * scale) / 2;
  const offY = (H - spanLat * scale) / 2;
  return {
    x: (lon) => offX + (lon - minLon) * lonScale * scale,
    y: (lat) => H - offY - (lat - minLat) * scale,
  };
}

export const MiniMap: React.FC<{
  device: EventLiveDevice | null;
  racelineCoords: Array<[number, number]> | null;
  trailTrack: GpsSummary['track'] | null;
  /** Playback position (UTC ms) — when the trail has timestamps the
   *  car dot tracks the PLAYHEAD, not the live SSE position, so replay
   *  shows where the car was in the footage. */
  playheadUtc?: number | null;
  /** Click-to-seek: clicking the track seeks playback to the moment
   *  the car was nearest that point (nearest timestamped trail point —
   *  the poor man's project-to-route). */
  onSeek?: (utcMs: number) => void;
  /** Top-left context chip, e.g. event/stage name. */
  label?: string | null;
}> = ({ device, racelineCoords, trailTrack, playheadUtc = null, onSeek, label = null }) => {
  const geometry = useMemo<Array<[number, number]>>(() => {
    if (racelineCoords && racelineCoords.length > 1) return racelineCoords;
    if (trailTrack && trailTrack.length > 1) {
      return trailTrack.map((p) => [p[0], p[1]] as [number, number]);
    }
    if (device?.has_fix) return [[device.lat, device.lon]];
    return [];
  }, [racelineCoords, trailTrack, device]);

  const proj = useMemo(() => buildProjector(geometry), [geometry]);

  const path = useMemo(() => {
    if (!proj || geometry.length < 2) return null;
    return geometry
      .map(([lat, lon], i) => `${i === 0 ? 'M' : 'L'}${proj.x(lon).toFixed(1)} ${proj.y(lat).toFixed(1)}`)
      .join(' ');
  }, [proj, geometry]);

  // Car dot: playhead-tracked when the trail carries timestamps
  // (track tuples are [lat, lon, utc, …]); falls back to live SSE pos.
  const car = useMemo(() => {
    if (!proj) return null;
    if (trailTrack && trailTrack.length > 0 && playheadUtc != null) {
      let best = trailTrack[0];
      let bestDt = Math.abs((best[2] ?? 0) - playheadUtc);
      for (const p of trailTrack) {
        const dt = Math.abs((p[2] ?? 0) - playheadUtc);
        if (dt < bestDt) {
          bestDt = dt;
          best = p;
        }
      }
      return { x: proj.x(best[1]), y: proj.y(best[0]) };
    }
    if (!device?.has_fix) return null;
    return { x: proj.x(device.lon), y: proj.y(device.lat) };
  }, [proj, device, trailTrack, playheadUtc]);

  // Click → nearest timestamped trail point → seek there.
  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!onSeek || !proj || !trailTrack || trailTrack.length === 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const py = ((e.clientY - rect.top) / rect.height) * H;
    let bestUtc: number | null = null;
    let bestD = Infinity;
    for (const p of trailTrack) {
      const utc = p[2];
      if (utc == null) continue;
      const dx = proj.x(p[1]) - px;
      const dy = proj.y(p[0]) - py;
      const d = dx * dx + dy * dy;
      if (d < bestD) {
        bestD = d;
        bestUtc = utc;
      }
    }
    // Ignore clicks far from the track (>~20 viewBox px).
    if (bestUtc != null && bestD <= 400) onSeek(bestUtc);
  };

  return (
    <div
      className={
        'relative shrink-0 overflow-hidden border-b border-border-subtle' +
        (onSeek && trailTrack && trailTrack.length > 0 ? ' cursor-crosshair' : '')
      }
      onClick={handleClick}
      title={onSeek && trailTrack && trailTrack.length > 0 ? 'Click the track to seek' : undefined}
      style={{
        aspectRatio: '16/10',
        background: 'linear-gradient(135deg,#0a1820,#0a0f15)',
        backgroundImage:
          'linear-gradient(rgba(56,189,248,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(56,189,248,.04) 1px,transparent 1px)',
        backgroundSize: '18px 18px',
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 h-full w-full"
      >
        {path && (
          <>
            <path d={path} stroke="#06090d" strokeWidth={7} fill="none" strokeLinecap="round" opacity={0.7} />
            <path
              d={path}
              stroke="#38bdf8"
              strokeWidth={2.5}
              fill="none"
              strokeDasharray="5 4"
              opacity={0.55}
            />
          </>
        )}
        {car && (
          <>
            <circle cx={car.x} cy={car.y} r={5} fill="var(--accent-primary)" stroke="#fff" strokeWidth={1.5}>
              <animate attributeName="opacity" values="1;.55;1" dur="1.8s" repeatCount="indefinite" />
            </circle>
          </>
        )}
      </svg>
      {car && device && (
        <div
          className="absolute -translate-x-1/2 rounded bg-accent px-1.5 py-px font-mono text-[10px] font-bold text-fg-inverse shadow"
          style={{
            left: `${(car.x / W) * 100}%`,
            top: `${(car.y / H) * 100}%`,
            transform: 'translate(-50%, -160%)',
          }}
        >
          {displayCarNumber(device)}
        </div>
      )}
      {label && (
        <div className="absolute left-2 top-2 rounded bg-black/65 px-1.5 py-0.5 font-mono text-[9px] font-bold text-white">
          {label}
        </div>
      )}
      {!path && !car && (
        <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] text-fg-muted">
          no GPS yet
        </div>
      )}
    </div>
  );
};

export default MiniMap;
